import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./DemandesList.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Normalisation simple (évite les soucis d'accents / casse / espaces)
function norm(s) {
  return (s ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function itemSlugFromDemande(d) {
  const statut = norm(d?.statut_demande);

  if (statut === norm("EACI")) return "eaci";
  if (statut === norm("Demandes orientées")) return "demandes-orientees";
  if (statut === norm("Demandes annulées")) return "demandes-annulees";
  if (statut === norm("Liste d'attente")) return "liste-attente";
  if (statut === norm("Retour décision CDS")) return "retour-decision-cds";
  if (statut === norm("Fléchage") || statut === norm("Flechage"))
    return "flechage";

  return "demandes-transmises";
}

function pillClassFromStatut(statut) {
  const s = norm(statut);
  if (!s) return "pill pill--muted";
  if (s === norm("EACI")) return "pill pill--eaci";
  if (s === norm("Fléchage") || s === norm("Flechage"))
    return "pill pill--flechage";
  if (s === norm("Retour décision CDS")) return "pill pill--retour";
  if (s === norm("Demandes orientées")) return "pill pill--success";
  if (s === norm("Liste d'attente")) return "pill pill--retour";
  if (s === norm("Demandes annulées")) return "pill pill--danger";
  return "pill";
}

// couleur opérateur
function operateurColor(op) {
  const o = (op || "").toString().trim().toUpperCase();
  if (o === "AMEL") return "red";
  if (o === "LAMIA") return "#A020F0";
  if (o === "CLAUDIA") return "blue";
  if (o === "SOLENNE") return "orange";
  if (o === "MORGAN") return "green";
  if (o === "ELISSA") return "pink";
  return "";
}

/**
 * ✅ Parse dates FR "DD/MM/YYYY" (et "DD/MM/YYYY HH:mm")
 */
function parseTransmissionToMs(raw) {
  if (!raw) return 0;
  const s = String(raw).trim();

  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
  if (m) {
    const dd = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10) - 1;
    const yyyy = parseInt(m[3], 10);
    const hh = m[4] ? parseInt(m[4], 10) : 0;
    const mi = m[5] ? parseInt(m[5], 10) : 0;

    const d = new Date(yyyy, mm, dd, hh, mi, 0, 0);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  }

  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
}

function transmissionTime(d) {
  return parseTransmissionToMs(d?.date_premiere_transmission);
}

// format FR de la date de transmission
function formatTransmissionFR(raw) {
  const ms = parseTransmissionToMs(raw);
  if (!ms) return "-";
  return new Date(ms).toLocaleDateString("fr-FR");
}

// ✅ Retour EACI : date + nb de jours depuis le retour
function retourEaciTime(d) {
  return parseTransmissionToMs(d?.retour_eaci_at);
}

function daysSinceMs(ms) {
  if (!ms) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  const diff = today.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
}

export default function DemandesList({ onSelect, refreshKey, searchText, onCounts }) {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // cache pour éviter de re-télécharger /api/demandes pendant la recherche
  const [allCache, setAllCache] = useState({ key: null, list: null });

  // évite de relancer le fetch juste parce que onCounts change d’identité
  const onCountsRef = useRef(onCounts);
  useEffect(() => {
    onCountsRef.current = onCounts;
  }, [onCounts]);

  const q = norm(searchText);
  const mode = q ? "all" : "accueil";

  useEffect(() => {
    const fetchDemandes = async () => {
      setLoading(true);
      setError("");

      try {
        let list = [];

        if (mode === "accueil") {
          // léger : sans opérateur + retours EACI
          const res = await fetch(`${API}/api/demandes/accueil`);
          if (!res.ok) {
            const txt = await res.text();
            throw new Error(txt || "Erreur serveur");
          }
          const data = await res.json();
          list = Array.isArray(data) ? data : [];
          setDemandes(list);
        } else {
          // global : on le télécharge une seule fois par refreshKey
          if (allCache.list && allCache.key === refreshKey) {
            list = allCache.list;
            setDemandes(list);
          } else {
            const res = await fetch(`${API}/api/demandes`);
            if (!res.ok) {
              const txt = await res.text();
              throw new Error(txt || "Erreur serveur");
            }
            const data = await res.json();
            list = Array.isArray(data) ? data : [];
            setAllCache({ key: refreshKey, list });
            setDemandes(list);
          }
        }

        // ✅ remonte le nombre de demandes sans opérateur + délai de traitement (hors EACI)
        if (typeof onCountsRef.current === "function") {
          const unassignedCount = list.filter(
            (d) => !d.operateur || String(d.operateur).trim() === ""
          ).length;

          // On calcule le délai sur la logique "accueil" : non attribuées + retours EACI, hors EACI
          const accueilList = list.filter((d) => {
            const unassigned = !d.operateur || String(d.operateur).trim() === "";
            const retour = !!d.retour_eaci;
            return unassigned || retour;
          });

          const eligible = accueilList.filter(
            (d) => norm(d?.statut_demande) !== norm("EACI")
          );

          let oldest = 0;
          for (const d of eligible) {
            const t = transmissionTime(d);
            if (!t) continue;
            if (!oldest || t < oldest) oldest = t;
          }

          const msDay = 1000 * 60 * 60 * 24;
          let delayDays = 0;
          let delayFrom = null;

          if (oldest) {
            const today0 = new Date();
            today0.setHours(0, 0, 0, 0);

            const old0 = new Date(oldest);
            old0.setHours(0, 0, 0, 0);

            const diff = today0.getTime() - old0.getTime();
            delayDays = diff > 0 ? Math.floor(diff / msDay) : 0;
            delayFrom = new Date(oldest).toISOString();
          }

          onCountsRef.current({
            unassigned: unassignedCount,
            delay_days: delayDays,
            delay_from: delayFrom,
          });
        }
      } catch (e) {
        console.error(e);
        setError("Impossible de charger les demandes.");
        setDemandes([]);

        if (typeof onCountsRef.current === "function") {
          onCountsRef.current({
            unassigned: 0,
            delay_days: 0,
            delay_from: null,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDemandes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, mode]);

  const demandesAffichees = useMemo(() => {
    let base = demandes;

    // Barre vide : non attribuées + retours EACI
    if (!q) {
      base = base.filter((d) => {
        const unassigned = !d.operateur || String(d.operateur).trim() === "";
        const retour = !!d.retour_eaci;
        return unassigned || retour;
      });
    } else {
      // Barre remplie : recherche globale
      base = base.filter((d) => {
        const nom = norm(d.nom_naissance);
        const prenom = norm(d.prenom);
        const numero = norm(d.numero_demande);
        const operateur = norm(d.operateur);
        const statut = norm(d.statut_demande);

        return (
          nom.includes(q) ||
          prenom.includes(q) ||
          numero.includes(q) ||
          operateur.includes(q) ||
          statut.includes(q)
        );
      });
    }

    // ✅ TRI :
    // 1) EACI en tête
    // 2) le reste par transmission (PLUS ANCIEN -> PLUS RÉCENT)
    const sorted = [...base].sort((a, b) => {
      const aEaci = norm(a?.statut_demande) === norm("EACI");
      const bEaci = norm(b?.statut_demande) === norm("EACI");

      if (aEaci && !bEaci) return -1;
      if (!aEaci && bEaci) return 1;

      const ta = transmissionTime(a);
      const tb = transmissionTime(b);
      if (ta !== tb) return ta - tb;

      const ca = new Date(a?.createdAt || 0).getTime() || 0;
      const cb = new Date(b?.createdAt || 0).getTime() || 0;
      return ca - cb;
    });

    return sorted;
  }, [demandes, q]);

  const handleClick = (d) => {
    const hasOperateur = !!(d.operateur && String(d.operateur).trim() !== "");

    // Si déjà attribuée : ouvrir détail opérateur
    if (hasOperateur) {
      const id = d._id;

      const flechageTarget =
        d.flechage && String(d.flechage).trim() !== ""
          ? String(d.flechage).trim()
          : "";

      if (flechageTarget) {
        navigate(
          `/operateur/${encodeURIComponent(
            flechageTarget
          )}/flechage/${encodeURIComponent(id)}`
        );
        return;
      }

      const operateurOwner = String(d.operateur).trim();
      const itemSlug = itemSlugFromDemande(d);

      navigate(
        `/operateur/${encodeURIComponent(
          operateurOwner
        )}/${itemSlug}/${encodeURIComponent(id)}`
      );
      return;
    }

    // Sinon : non attribuée -> détail accueil
    if (typeof onSelect === "function") {
      onSelect(d);
      return;
    }

    navigate(`/demande/${d._id}`);
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div className="demandes-list">
      {demandesAffichees.map((d) => {
        const isRetourEaci =
          !!d.retour_eaci && d.operateur && String(d.operateur).trim() !== "";

        const nomPrenom = `${d.nom_naissance || ""} ${d.prenom || ""}`.trim();
        const operateur = (d.operateur || "").toString().trim();
        const statut = d.statut_demande || "";
        const transmission = formatTransmissionFR(d.date_premiere_transmission);

        const reMs = isRetourEaci ? retourEaciTime(d) : 0;
        const reDays = reMs ? daysSinceMs(reMs) : 0;
        const reDate = reMs ? new Date(reMs).toLocaleDateString("fr-FR") : "";
        const retourEaciLabel = reMs
          ? `Retour EACI (${reDays} j) — ${reDate}`
          : "Retour EACI";

        return (
          <div
            key={d._id}
            className="demande-item"
            onClick={() => handleClick(d)}
          >
            <div className="demande-left">
              <div className="demande-title">{nomPrenom || "—"}</div>

              <div className="demande-sub">
                {d.numero_demande ? (
                  <span className="pill pill--muted">#{d.numero_demande}</span>
                ) : null}

                <span className="pill pill--muted">
                  Transmission : {transmission}
                </span>

                {operateur ? (
                  isRetourEaci ? (
                    <span
                      className="pill pill--muted"
                      style={{
                        color: operateurColor(operateur) || undefined,
                        fontWeight: 950,
                      }}
                    >
                      {operateur}
                    </span>
                  ) : (
                    <span className="pill pill--muted">{operateur}</span>
                  )
                ) : null}

                {statut ? (
                  <span className={pillClassFromStatut(statut)}>{statut}</span>
                ) : null}
              </div>
            </div>

            <div className="demande-right">
              {isRetourEaci ? (
                <span className="pill pill--danger">{retourEaciLabel}</span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
